import { ClientOnly, createFileRoute, isNotFound, Link } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { useTranslation } from '@/lib/i18n';
import { documentTypeMessages, errorMessages } from '@/lib/portal-messages';
import { Title } from '@worldcoo/ui/components/form/title';
import { Paragraph } from '@worldcoo/ui/components/form/paragraph';
import { useAppForm } from '@/lib/use-app-form';
import { Animate } from '@worldcoo/ui/custom/animate';
import { buttonVariants } from '@worldcoo/ui/components/button';
import { Spinner } from '@worldcoo/ui/components/spinner';
import { usePersistedStore, useEphemeralStore } from '@/lib/app-store';
import { detailsSchema } from '@/lib/details-schema';
import { useEffect, useRef, useState, type RefObject } from 'react';
import { usePortalCampaignQuery, usePortalAuthorQuery } from '@/lib/data-hooks';
import { trpc, trpcClient } from '@/lib/trpc';

import { formatList, cn, formatNumber, savedTaxAmount } from '@worldcoo/ui/lib/utils';
import { BeneficiariesIcon } from '@worldcoo/ui/custom/beneficiaries-icon';
import { revalidateLogic } from '@tanstack/react-form';
import { TermsAndConditionsDialog } from '../-components/terms-and-conditions';
import { TermsCheckboxField } from './-components/terms-checkbox-field';
import { useMutation } from '@tanstack/react-query';
import { generateCaptchaToken } from '@worldcoo/ui/lib/captcha';
import { applicationType } from '@worldcoo/monolith/shared-context';
import { amountToMinorUnits } from '@worldcoo/shared/currencies';
import { localeToLanguage } from '@worldcoo/shared/core';
import { toast } from '@/lib/toast';

export const Route = createFileRoute('/$portalId/$campaignId/donate/details')({
	head: () => ({ meta: [{ name: 'robots', content: 'noindex' }] }),
	component: Component,
});

function Component() {
	const { portalId, campaignId } = Route.useParams();
	const navigate = Route.useNavigate();
	const { language } = useTranslation();
	const { t } = useLingui();
	const countryFieldTexts = {
		search: t`Search...`,
		popular: t`Popular`,
		allCountries: t`All countries`,
		noCountryFound: t`No country found.`,
	};
	const regionFieldTexts = { search: t`Search...`, noResults: t`No results found.` };
	const provinceFieldTexts = { search: t`Search...`, noResults: t`No results found.` };
	const defaultValues = usePersistedStore((state) => state.details);
	const amount = usePersistedStore((state) => state.amount);
	const hasHydrated = usePersistedStore((state) => state._hasHydrated);
	const setDetails = usePersistedStore((state) => state.setDetails);
	const termsContent = usePersistedStore((state) => state.termsContent);
	const ngosTermsContent = usePersistedStore((state) => state.ngosTermsContent);
	const telefonicaTermsContent = usePersistedStore((state) => state.telefonicaTermsContent);
	const setCachedPreregistration = useEphemeralStore((state) => state.setCachedPreregistration);
	const setCachedPaymentMethods = useEphemeralStore((state) => state.setCachedPaymentMethods);

	const { mutateAsync: preregisterDonation } = useMutation(trpc.payments.preregisterDonation.mutationOptions());

	const currencyCode = 'EUR'; // TODO!!!

	const form = useAppForm({
		validators: { onChange: detailsSchema(portalId) },
		validationLogic: revalidateLogic({
			mode: 'submit',
			modeAfterSubmission: 'change',
		}),
		defaultValues: portalId === 'telefonica' ? { ...defaultValues, certificate: true } : defaultValues,
		listeners: {
			onChangeDebounceMs: 300,
			onChange: ({ formApi }) => {
				setDetails(formApi.state.values);
			},
		},
		onSubmit: async ({ value }) => {
			setDetails(value);

			// Trigger preregistration and payment methods fetching
			await Promise.all([
				generateCaptchaToken().then((captchaToken) => {
					if (!amount) return;

					return preregisterDonation({
						certificated: value.certificate,
						amount: amount,
						campaignId,
						currency: currencyCode,
						applicationId: {
							type: applicationType.portal,
							host: portalId,
						},
						donor: {
							name: value.firstname,
							lastName: value.lastname,
							contact: {
								email: value.email,
								language: localeToLanguage[language],
								physicalAddress: {
									region: value.region,
									street: {
										name: value.streetName,
										number: value.streetNumber,
										type: '',
									},
									additionalInfo: value.additionalInfo,
									city: value.city,
									country: value.country,
									postalCode: value.cp,
									province: value.province,
								},
							},
							document: {
								number: value.documentNumber,
								type: value.documentType,
							},
							subscribeNewsletter: value.newsletter,
						},
						checkbox: [
							termsContent && { type: 'termsAndConditions' as const, ...termsContent },
							ngosTermsContent && { type: 'ngoTermsAndConditions' as const, ...ngosTermsContent },
							telefonicaTermsContent && {
								type: 'telefonicaTermsAndConditions' as const,
								...telefonicaTermsContent,
							},
						].filter((v) => v !== undefined),
						captchaToken,
					}).then((result) => {
						if (result?.captchaPassed) {
							setCachedPreregistration(result);
						}
					});
				}),
				generateCaptchaToken().then((captchaToken) => {
					if (!amount) return;

					return trpcClient.payments.getPaymentMethods
						.mutate({
							amount: { value: amountToMinorUnits(amount, currencyCode), currency: currencyCode },
							countryCode: value.country,
							locale: language,
							captchaToken,
						})
						.then((paymentMethods) => {
							if (paymentMethods.captchaPassed) {
								setCachedPaymentMethods(paymentMethods);
							}
						});
				}),
			]).catch((error) => {
				console.error('Background preregistration failed:', error);
				toast.error(t(errorMessages.internal));
			});

			await navigate({ to: `/$portalId/$campaignId/donate/checkout`, params: { portalId, campaignId } });
		},
	});

	useEffect(() => {
		if (hasHydrated && !amount) navigate({ to: `/$portalId/$campaignId/donate`, params: { portalId, campaignId } });
	}, [amount, navigate, campaignId, hasHydrated, portalId]);

	const campaign = usePortalCampaignQuery(campaignId);
	const author = usePortalAuthorQuery();
	const setTermsContent = usePersistedStore((state) => state.setTermsContent);
	const setNgosTermsContent = usePersistedStore((state) => state.setNgosTermsContent);
	const setTelefonicaTermsContent = usePersistedStore((state) => state.setTelefonicaTermsContent);

	const [openTermsDialog, setOpenTermsDialog] = useState(false);
	const [openNgosTermsDialog, setOpenNgosTermsDialog] = useState(false);
	const [openTelefonicaTermsDialog, setOpenTelefonicaTermsDialog] = useState(false);

	const termsLabelRef = useRef<HTMLDivElement>(null);
	const termsDialogRef = useRef<HTMLDivElement>(null);
	const termsTextRef = useRef<HTMLTableElement>(null);
	const hiddenTermsTextRef = useRef<HTMLTableElement>(null);
	const ngosTermsLabelRef = useRef<HTMLDivElement>(null);
	const ngosTermsDialogRef = useRef<HTMLDivElement>(null);
	const ngosTermsTextRef = useRef<HTMLTableElement>(null);
	const hiddenNgosTermsTextRef = useRef<HTMLTableElement>(null);
	const telefonicaTermsLabelRef = useRef<HTMLDivElement>(null);
	const telefonicaTermsDialogRef = useRef<HTMLDivElement>(null);
	const telefonicaTermsTextRef = useRef<HTMLDivElement>(null);
	const hiddenTelefonicaTermsTextRef = useRef<HTMLDivElement>(null);

	const documentTypeOptions = Object.entries(documentTypeMessages).map(([value, descriptor]) => ({
		value,
		label: t(descriptor),
	}));

	if (isNotFound(campaign) || isNotFound(author)) return null;

	const ngoNames = formatList(
		language,
		campaign.data.ngos.map((ngo) => ngo.name ?? ''),
	);

	return (
		<Animate>
			<form
				onSubmit={(event) => {
					event.preventDefault();
					form.handleSubmit();
				}}
				className="flex flex-col gap-6"
			>
				<div className="flex flex-col gap-2">
					<Title size="lg" className="text-center">
						{t`Add your details to proceed`}
					</Title>
					<Paragraph className="text-center">{t`Fill in your details to complete your donation. You can also request your tax certificate here.`}</Paragraph>
				</div>
				<div className="flex flex-col gap-3">
					<div className="flex flex-col gap-3 sm:flex-row">
						<form.AppField
							name="firstname"
							children={(field) => <field.TextField label={t`Name`} variant="outline" className="w-full" />}
						/>
						<form.AppField
							name="lastname"
							children={(field) => <field.TextField label={t`Last Name`} variant="outline" className="w-full" />}
						/>
					</div>
					<form.AppField name="email" children={(field) => <field.TextField label={t`Email`} variant="outline" />} />
					<form.AppField
						name="repeatEmail"
						children={(field) => <field.TextField label={t`Repeat your email`} variant="outline" />}
					/>
				</div>
				<form.AppField
					name="certificate"
					children={(field) => (
						<label
							className={cn(
								'bg-primary/10 flex items-center gap-3 rounded-[12px] border border-transparent px-4 py-3 transition-colors',
								field.state.value && 'border-primary shadow-primary/20',
								// Don't show certificate option if not available for the merchant (in this case all italian portals)
								language === 'it' && 'hidden',
							)}
							style={{
								boxShadow: field.state.value
									? `0 0 12px 0px color-mix(in oklab, color-mix(in oklab, var(--primary) 20%, transparent) var(--tw-shadow-alpha), transparent)`
									: undefined,
							}}
							htmlFor={field.name}
						>
							<div className="bg-background hidden rounded-sm p-2 lg:block">
								<BeneficiariesIcon className="text-primary shrink-0" />
							</div>

							<div className="font-light">
								<div className="flex items-center gap-2">
									<div
										dangerouslySetInnerHTML={{
											__html: (() => {
												const savedAmount = `<b class="font-semibold">${formatNumber(language, savedTaxAmount(amount!))} €</b>`;
												return t`Recover up to ${savedAmount}`;
											})(),
										}}
									/>
									{/* <Tooltip
										content={
											<>
												Según{' '}
												<a
													href="https://www.boe.es/buscar/act.php?id=BOE-A-2023-25758&p=20250103&tn=1#a1-41"
													target='_blank'
													rel='noopener noreferrer'
													className='underline'
												>
													Real Decreto-ley 6/2023 de 19 de diciembre
												</a>
											</>
										}
									>
										<InfoIcon className="-mt-[2px] inline size-4" />
									</Tooltip> */}
								</div>
								<p className="text-sm">{t`Certify your donation with one click and it will automatically appear on your tax return draft.`}</p>
							</div>

							<field.SwitchField size="lg" variant="primary" className="text-foreground mx-auto w-fit" />
						</label>
					)}
				/>
				<form.Subscribe
					selector={(state) => state.values.certificate}
					children={(certificate) => (
						<div className={cn('flex flex-col gap-3', !certificate && portalId !== 'telefonica' && 'hidden')}>
							<div className="flex flex-col gap-3 sm:flex-row">
								<form.AppField
									name="documentType"
									children={(field) => (
										<field.SelectTextField
											label={t`Document type`}
											options={documentTypeOptions}
											className="w-full"
											optional={!certificate}
										/>
									)}
								/>
								<form.AppField
									name="documentNumber"
									children={(field) => (
										<field.TextField
											label={t`Document number`}
											variant="outline"
											className="w-full"
											optional={!certificate}
										/>
									)}
								/>
							</div>
							<form.AppField
								name="streetName"
								children={(field) => (
									<field.TextField label={t`Street name`} variant="outline" optional={!certificate} />
								)}
							/>
							<form.AppField
								name="streetNumber"
								children={(field) => <field.TextField label={t`Number`} variant="outline" optional={!certificate} />}
							/>
							<form.AppField
								name="additionalInfo"
								children={(field) => <field.TextField label={t`Additional information`} variant="outline" />}
							/>
							<form.AppField
								name="city"
								children={(field) => <field.TextField label={t`City`} variant="outline" optional={!certificate} />}
							/>
							<div className="flex flex-col gap-3 sm:flex-row">
								<form.AppField
									name="cp"
									children={(field) => (
										<field.TextField
											label={t`Postal code`}
											variant="outline"
											className="w-full"
											optional={!certificate}
										/>
									)}
								/>
								<form.AppField
									name="country"
									children={(field) => (
										<field.CountryField
											label={t`Country`}
											locale={language}
											variant="outline"
											texts={countryFieldTexts}
											className="w-full"
											optional={!certificate}
										/>
									)}
								/>
							</div>
							<ClientOnly>
								<form.AppField
									name="region"
									validators={{ onChangeListenTo: ['country'] }}
									listeners={{
										onChange: () =>
											form.setFieldValue('province', '', {
												dontValidate: true,
												dontUpdateMeta: true,
												dontRunListeners: true,
											}),
									}}
									children={(field) => (
										<field.RegionField
											label={t`Region`}
											country={form.getFieldValue('country')}
											texts={regionFieldTexts}
											optional={!certificate}
										/>
									)}
								/>
								<form.AppField
									name="province"
									validators={{ onChangeListenTo: ['country', 'region'] }}
									children={(field) => (
										<field.ProvinceField
											label={t`Province`}
											country={form.getFieldValue('country')}
											region={form.getFieldValue('region')}
											texts={provinceFieldTexts}
											optional={!certificate}
										/>
									)}
								/>
							</ClientOnly>
						</div>
					)}
				/>
				<div className="flex flex-col gap-5">
					<form.AppField
						name="termsAndConditions"
						listeners={{
							onChange: ({ value }) => {
								// When checkbox is toggled directly, capture text and label
								const currentContent = usePersistedStore.getState().termsContent;

								// If text and label are already set (e.g., from dialog accept), don't overwrite
								if (value && currentContent?.text && currentContent?.label) {
									return;
								}

								const textRef = termsTextRef.current || hiddenTermsTextRef.current;
								if (value && termsLabelRef.current && textRef) {
									setTermsContent({
										checked: true,
										label: termsLabelRef.current.innerHTML,
										text: textRef.innerHTML,
									});
								} else if (!value) {
									setTermsContent(undefined);
								}
							},
						}}
						children={(field) => (
							<TermsCheckboxField
								field={field}
								labelText={t`I have read, understand and accept the terms of `}
								linkText={(() => {
									const name = 'Worldcoo';
									return t`Data Protection of ${name}`;
								})()}
								dialogTitle={t`Data Protection Terms`}
								acceptButtonText={t`I have read, understand and accept`}
								open={openTermsDialog}
								onOpenChange={setOpenTermsDialog}
								labelRef={termsLabelRef}
								dialogRef={termsDialogRef}
								textRef={termsTextRef}
								termsContent={
									<TermsAndConditionsDialog
										ref={termsTextRef}
										responsible={t`WORLDCOO S.L.`}
										purpose={t`Manage the business relationship in all its areas, as well as your donation and your certificate request, and the sending of commercial communications by electronic means.`}
										legitimacy={t`The legal basis for processing your data is obtaining your consent.`}
										recipients={t`Data will not be transferred to third parties unless there is a legal obligation to do so.`}
										rights={t`You have the right to access, rectify and delete data, as well as other rights, as explained in the additional information.`}
										additionalInfo={t`You can consult additional and detailed information about Data Protection on our page <a href="https://www.worldcoo.com/privacy-policy">web</a> or by contacting <a href="mailto:rgpd@worldcoo.com">rgpd@worldcoo.com</a>.`}
									/>
								}
								onAccept={() => {
									if (!termsTextRef.current || !termsLabelRef.current) return;

									form.setFieldValue('termsAndConditions', true);
									setTermsContent({
										checked: true,
										label: termsLabelRef.current.innerHTML,
										text: termsTextRef.current.innerHTML,
									});
									setOpenTermsDialog(false);
								}}
							/>
						)}
					/>

					<form.AppField
						validators={{ onChangeListenTo: ['certificate'] }}
						name="ngosTermsAndConditions"
						listeners={{
							onChange: ({ value }) => {
								// When checkbox is toggled directly, capture text and label
								const currentContent = usePersistedStore.getState().ngosTermsContent;

								// If text and label are already set (e.g., from dialog accept), don't overwrite
								if (value && currentContent?.text && currentContent?.label) {
									return;
								}

								const textRef = ngosTermsTextRef.current || hiddenNgosTermsTextRef.current;
								if (value && ngosTermsLabelRef.current && textRef) {
									setNgosTermsContent({
										checked: true,
										label: ngosTermsLabelRef.current.innerHTML,
										text: textRef.innerHTML,
									});
								} else if (!value) {
									setNgosTermsContent(undefined);
								}
							},
						}}
						children={(field) => (
							<form.Subscribe
								selector={(state) => state.values.certificate}
								children={(certificate) => (
									<div className={cn(!certificate && 'hidden')}>
										<TermsCheckboxField
											field={field}
											labelText={t`I have read, understand and accept the terms of `}
											linkText={t`Data Protection of ${ngoNames}`}
											dialogTitle={t`Data Protection Terms`}
											acceptButtonText={t`I have read, understand and accept`}
											open={openNgosTermsDialog}
											onOpenChange={setOpenNgosTermsDialog}
											labelRef={ngosTermsLabelRef}
											dialogRef={ngosTermsDialogRef}
											textRef={ngosTermsTextRef}
											termsContent={
												<TermsAndConditionsDialog
													ref={ngosTermsTextRef}
													responsible={ngoNames}
													purpose={t`Fundraising for NGO projects, keeping you informed about the NGO and the evolution of the project for which you have donated funds and issuing tax deduction certificates.`}
													legitimacy={t`The legal basis for processing your data is obtaining your consent.`}
													recipients={t`Data will not be transferred to third parties unless there is a legal obligation to do so.`}
													rights={t`You have the right to access, rectify and delete data, as well as other rights, as explained in the additional information.`}
													additionalInfo={t`You can consult the additional and detailed information about Data Protection on each NGO's website.\n\n<b>${ngoNames}</b>`}
												/>
											}
											onAccept={() => {
												if (!ngosTermsTextRef.current || !ngosTermsLabelRef.current) return;

												form.setFieldValue('ngosTermsAndConditions', true);
												setNgosTermsContent({
													checked: true,
													label: ngosTermsLabelRef.current.innerHTML,
													text: ngosTermsTextRef.current.innerHTML,
												});
												setOpenNgosTermsDialog(false);
											}}
										/>
									</div>
								)}
							/>
						)}
					/>

					{portalId === 'telefonica' && (
						<form.AppField
							name="telefonicaTermsAndConditions"
							listeners={{
								onChange: ({ value }) => {
									// When checkbox is toggled directly, capture text and label
									const currentContent = usePersistedStore.getState().telefonicaTermsContent;

									// If text and label are already set (e.g., from dialog accept), don't overwrite
									if (value && currentContent?.text && currentContent?.label) {
										return;
									}

									const textRef = telefonicaTermsTextRef.current || hiddenTelefonicaTermsTextRef.current;
									if (value && telefonicaTermsLabelRef.current && textRef) {
										setTelefonicaTermsContent({
											checked: true,
											label: telefonicaTermsLabelRef.current.innerHTML,
											text: textRef.innerHTML,
										});
									} else if (!value) {
										setTelefonicaTermsContent(undefined);
									}
								},
							}}
							children={(field) => (
								<TermsCheckboxField
									field={field}
									labelText={t`I have read, understand and accept the terms of `}
									linkText={(() => {
										const authorName = author.name ?? '';
										return t`Data Protection of ${authorName}`;
									})()}
									dialogTitle={t`Data Protection Terms`}
									acceptButtonText={t`I have read, understand and accept`}
									open={openTelefonicaTermsDialog}
									onOpenChange={setOpenTelefonicaTermsDialog}
									labelRef={telefonicaTermsLabelRef}
									dialogRef={telefonicaTermsDialogRef}
									textRef={telefonicaTermsTextRef}
									hideUnresetTailwind
									termsContent={<TelefonicaTermsAndConditions ref={telefonicaTermsTextRef} />}
									onAccept={() => {
										if (!telefonicaTermsTextRef.current || !telefonicaTermsLabelRef.current) return;

										form.setFieldValue('telefonicaTermsAndConditions', true);
										setTelefonicaTermsContent({
											checked: true,
											label: telefonicaTermsLabelRef.current.innerHTML,
											text: telefonicaTermsTextRef.current.innerHTML,
										});
										setOpenTelefonicaTermsDialog(false);
									}}
								/>
							)}
						/>
					)}
				</div>
				<form.AppField
					name="newsletter"
					children={(field) => (
						<field.CheckboxField
							className="gap-3"
							label={
								<span className="text-dark-alt/80 font-normal -tracking-[0.2px]">{t`Subscribe to stay up to date with the causes we support, the projects we fund, and the people we empower around the world.`}</span>
							}
						/>
					)}
				/>
				{/* Hidden terms content for capturing refs when checkboxes are toggled directly */}
				<div className="hidden">
					<TermsAndConditionsDialog
						ref={hiddenTermsTextRef}
						responsible={t`WORLDCOO S.L.`}
						purpose={t`Manage the business relationship in all its areas, as well as your donation and your certificate request, and the sending of commercial communications by electronic means.`}
						legitimacy={t`The legal basis for processing your data is obtaining your consent.`}
						recipients={t`Data will not be transferred to third parties unless there is a legal obligation to do so.`}
						rights={t`You have the right to access, rectify and delete data, as well as other rights, as explained in the additional information.`}
						additionalInfo={t`You can consult additional and detailed information about Data Protection on our page <a href="https://www.worldcoo.com/privacy-policy">web</a> or by contacting <a href="mailto:rgpd@worldcoo.com">rgpd@worldcoo.com</a>.`}
					/>
					<form.Subscribe
						selector={(state) => state.values.certificate}
						children={(certificate) =>
							certificate ? (
								<TermsAndConditionsDialog
									ref={hiddenNgosTermsTextRef}
									responsible={ngoNames}
									purpose={t`Fundraising for NGO projects, keeping you informed about the NGO and the evolution of the project for which you have donated funds and issuing tax deduction certificates.`}
									legitimacy={t`The legal basis for processing your data is obtaining your consent.`}
									recipients={t`Data will not be transferred to third parties unless there is a legal obligation to do so.`}
									rights={t`You have the right to access, rectify and delete data, as well as other rights, as explained in the additional information.`}
									additionalInfo={t`You can consult the additional and detailed information about Data Protection on each NGO's website.\n\n<b>${ngoNames}</b>`}
								/>
							) : null
						}
					/>
					{portalId === 'telefonica' && <TelefonicaTermsAndConditions ref={hiddenTelefonicaTermsTextRef} />}
				</div>
				<form.Subscribe
					selector={(state) => state.isSubmitting}
					children={(isSubmitting) => (
						<Link
							to="/$portalId/$campaignId/donate/checkout"
							params={{ portalId, campaignId }}
							className={cn(
								buttonVariants({ size: '2xl' }),
								'mx-auto mt-6 w-full min-w-fit lg:w-60',
								isSubmitting && 'pointer-events-none opacity-50',
							)}
							aria-disabled={isSubmitting}
							onClick={(e) => {
								// The form owns validation + preregistration + navigation;
								// the Link is here so the router preloads the checkout head.
								e.preventDefault();
								form.handleSubmit();
							}}
						>
							{isSubmitting && <Spinner />}
							{t`Next`}
						</Link>
					)}
				/>
			</form>
		</Animate>
	);
}

function TelefonicaTermsAndConditions(props: { ref: RefObject<HTMLDivElement | null> }) {
	return (
		<div
			ref={props.ref}
			className="unreset-tailwind"
			dangerouslySetInnerHTML={{
				__html:
					'<p><strong>AVISO LEGAL PARA LA TOMA DE DATOS DE CARÁCTER PERSONAL</strong></p><p>FUNDACIÓN TELEFÓNICA respeta la legislación vigente en materia de protección de datos personales, la privacidad de los usuarios y el secreto y seguridad de los datos personales, en concreto el Reglamento 2016/679 del Parlamento Europeo y del Consejo de 27 de abril de 2016, adoptando para ello las medidas técnicas y organizativas necesarias para evitar la pérdida, mal uso, alteración, acceso no autorizado y robo de los datos personales facilitados, habida cuenta del estado de la tecnología, la naturaleza de los datos y los riesgos a los que están expuestos.</p><p>Al efectuar tu donativo, los datos que facilites (nombre y apellidos, correo electrónico) serán tratados por Fundación Telefónica, con la finalidad de gestionar tu participación en esta iniciativa y, en su caso, enviarte comunicaciones de tu interés en relación al mismo y al Programa Voluntarios Telefónica. Tus datos podrán ser cedidos a terceros, para coordinar tu participación en la actividad. En el caso de ser empleado de una empresa del grupo Telefónica, Fundación Telefónica o Fundación ProFuturo, tus datos podrán ser cedidos a la correspondiente área de Personas de dichas entidades, para coordinar y gestionar tu participación en esta actividad de voluntariado corporativo.</p><p>&nbsp;</p><p>De acuerdo con la actual legislación, queda informado y acepta el tratamiento de sus datos con la finalidad descrita a continuación:</p><p>&nbsp;</p><p><strong>Información sobre protección de datos</strong></p><p>Responsable:&nbsp;Fundación Telefónica.</p><p>Finalidad del Tratamiento:&nbsp;Recogida de los datos de donantes que contribuyen a causas promovidas por Fundación Telefónica. Como entidad sin ánimo de lucro y dentro del desarrollo de sus fines fundacionales, Fundación Telefónica le mantendrá informado de su actividad.</p><p>Legitimación:&nbsp;Consentimiento del interesado y obligación legal</p><p>Cesiones:&nbsp;Administración tributaria.</p><p>Derechos:&nbsp;A retirar su consentimiento en cualquier momento, a oponerse al tratamiento, a acceder, rectificar y suprimir los datos, así como otros derechos, tal y como se explica en la información adicional.</p><p>Transferencias Internacionales:&nbsp;No se realizan transferencias internacionales de datos de carácter personal.</p><p>Información Adicional:&nbsp;<a href="https://legal.fundaciontelefonica.com/informacion-adicional-proteccion-de-datos/" target="_blank" rel="noopener noreferrer" class="underline">https://legal.fundaciontelefonica.com/informacion-adicional-proteccion-de-datos/</a></p>',
			}}
		/>
	);
}
